<template>
  <router-view />
</template>

<script lang="ts" setup>
import { useFlag } from '@unleash/proxy-client-vue';
import { computed, watch } from 'vue';
import { useRouter } from 'vue-router';

const ROUTES = {
  PHASE_1: 'routeBuilderPhase1',
  PHASE_2: 'routeBuilderPhase2',
};

const isTransportSegmentEnabled = useFlag(
  'route-builder-transport-segment-feat-flag',
);

const router = useRouter();

const queryParams = computed(() => router.currentRoute.value.query);

watch(
  () => router.currentRoute.value.name,
  (newVal) => {
    if (!newVal || (newVal !== ROUTES.PHASE_1 && newVal !== ROUTES.PHASE_2)) {
      router.replace({
        name: isTransportSegmentEnabled.value ? ROUTES.PHASE_2 : ROUTES.PHASE_1,
        query: queryParams.value,
      });
    }
  },
);

watch(
  isTransportSegmentEnabled,
  (newVal, oldVal) => {
    if (newVal === oldVal) {
      return;
    }

    if (newVal) {
      router.replace({ name: ROUTES.PHASE_2, query: queryParams.value });
    } else {
      router.replace({ name: ROUTES.PHASE_1, query: queryParams.value });
    }
  },
  { immediate: true },
);
</script>

<style>
/*
Shared styling for both RouteBuilderPhase1 and RouteBuilderPhase2
If either needs distinct styling, put it in the relevant view in a specific
selector, i.e. `.route-builder.phase-1`
*/
.route-builder {
  --content-container-height: 100%;
  --route-builder-rate-shop-padding: 2px;

  &.has-error {
    #shipments {
      .rygen-button[disabled] {
        cursor: not-allowed;
      }
    }
  }

  .content-header {
    /* Same as visually-hidden to keep the element in the DOM but hidden */
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    border: 0;
    padding: 0;
    white-space: nowrap;
    clip-path: inset(100%);
    clip: rect(0 0 0 0);
    overflow: hidden;
    /* end visually-hidden rules */
  }

  .content-main {
    height: 100%;

    > .p-card {
      height: 100%;

      .p-card-body {
        height: 100%;

        .p-card-content {
          position: relative;
          height: 100%;

          .p-splitter {
            border: 0;
            height: 100%;
            width: 100%;

            &.p-splitter-horizontal {
              & > .p-splitter-gutter {
                /*
              full height minus --sp-6 padding on top;
              We don't need to worry about bottom padding because the container
              has a padding of sp-6
              */
                height: calc(100% - var(--sp-6));
                align-self: end;
              }

              .section-data {
                height: 100%;

                /* TODO: Try using an IntersectionObserver to increase/decrease the number of table rows */
              }
            }

            .detail-section {
              --section-padding: 0;

              display: flex;
              flex-flow: column;
              height: 100%;
            }

            .view-route-panel {
              padding: 0 0 var(--sp-6) 0;

              &:has(.size-collapsed) {
                padding: 0 0 var(--sp-3) 0;
                /* `!important` to override element style */
                flex: 0 1 auto !important;
              }

              #view-route {
              }
            }

            .planning-pool-panel {
              padding: var(--sp-6) var(--sp-6) 0 0;

              #planning-pool {
              }
            }

            .shipments-panel {
              padding: var(--sp-6) 0 0 var(--sp-6);

              #shipments {
                flex: 1 1 auto;
              }
            }
          }

          #rate-shop,
          #execution {
            --section-padding: var(--sp-6) 0 0 0;
          }
        }
      }
    }
  }

  .initial-bottom-row-enter-active,
  .initial-bottom-row-leave-active {
    transition:
      opacity 0.5s ease,
      transform 0.5s ease;
    top: var(--sp-12);
  }

  .initial-bottom-row-leave-active.initial-bottom-row-leave-to {
    position: absolute;
  }

  .initial-bottom-row-leave-to {
    opacity: 0;
    transform: translateX(-1000px);
  }

  .rate-shop-transition-enter-active {
    transition:
      opacity 0.25s ease 0.25s,
      transform 0.25s ease 0.25s;
  }

  .rate-shop-transition-enter-from {
    transform: translateX(400px);
    opacity: 0;
  }

  .rate-shop-transition-enter-to {
    transform: none;
    opacity: 1;
  }
}
</style>
